import {
    backdropClasses,
    filledInputClasses,
    outlinedInputClasses,
    paperClasses,
    tableCellClasses
} from '@mui/material'
import { common } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'

const defaultTweaks = {
    appBarAlpha: 0.9,
    paperAlpha: 0.94,
    elevatedSurfaceAlpha: 0.94,
    borderAlpha: 0.12,
    hoverBorderAlpha: 0.2,
    overlayBorderAlpha: 0.14,
    tableHeadAlpha: 0.06
}

export const createDarkFamilyComponents = ({ palette, tweaks = {} }) => {
    const options = { ...defaultTweaks, ...tweaks }

    return {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(common.black, options.appBarAlpha),
                    color: palette.text.primary,
                    borderBottom: `1px solid ${alpha(common.white, options.borderAlpha)}`,
                    backdropFilter: 'blur(10px)',
                    backgroundImage: `linear-gradient(180deg, ${alpha(
                        palette.primary.main,
                        0.05
                    )} 0%, transparent 100%)`
                }
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    border: '1px solid',
                    borderColor: alpha(common.white, options.borderAlpha),
                    backgroundColor: alpha(common.white, 0.015),
                    '&:before': {
                        display: 'none'
                    }
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(common.white, 0.08),
                    color: palette.text.primary
                }
            }
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    [`&:not(.${backdropClasses.invisible})`]: {
                        backgroundColor: alpha(common.black, 0.64),
                        backdropFilter: 'blur(6px)'
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    letterSpacing: '-0.01em'
                },
                contained: {
                    boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.3)',
                    '&:hover': {
                        boxShadow: '0px 14px 30px rgba(0, 0, 0, 0.36)'
                    }
                },
                outlined: {
                    borderColor: alpha(common.white, 0.14),
                    backgroundColor: alpha(common.white, 0.02),
                    '&:hover': {
                        borderColor: alpha(palette.primary.main, 0.32),
                        backgroundColor: alpha(palette.primary.main, 0.08)
                    }
                },
                text: {
                    '&:hover': {
                        backgroundColor: alpha(palette.primary.main, 0.08)
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    border: `1px solid ${alpha(common.white, options.borderAlpha)}`,
                    backgroundColor: alpha(
                        palette.background.paper,
                        options.elevatedSurfaceAlpha
                    ),
                    backgroundImage: `linear-gradient(180deg, ${alpha(
                        common.white,
                        0.01
                    )} 0%, transparent 100%)`,
                    transition:
                        'box-shadow 180ms ease, border-color 180ms ease',
                    [`&.${paperClasses.elevation1}`]: {
                        boxShadow:
                            '0px 16px 36px rgba(0, 0, 0, 0.26), 0px 2px 10px rgba(0, 0, 0, 0.2)'
                    },
                    '&:hover': {
                        borderColor: alpha(
                            palette.primary.main,
                            options.hoverBorderAlpha
                        ),
                        boxShadow:
                            '0px 20px 44px rgba(0, 0, 0, 0.3), 0px 4px 14px rgba(0, 0, 0, 0.22)'
                    }
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                icon: {
                    color: palette.action.active
                },
                root: {
                    borderColor: alpha(common.white, 0.14),
                    backgroundColor: alpha(common.white, 0.03)
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    background:
                        `radial-gradient(circle at 0% 0%, ${alpha(
                            palette.primary.main,
                            0.12
                        )} 0%, transparent 40%), ` +
                        `radial-gradient(circle at 100% 0%, ${alpha(
                            common.white,
                            0.03
                        )} 0%, transparent 35%), ` +
                        palette.background.default
                },
                '#nprogress .bar': {
                    backgroundColor: palette.primary.main
                },
                '.slick-dots li button': {
                    '&:before': {
                        fontSize: 10,
                        color: palette.primary.main
                    }
                },
                '.slick-dots li.slick-active button': {
                    '&:before': {
                        color: palette.primary.main
                    }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    border: `1px solid ${alpha(common.white, options.overlayBorderAlpha)}`,
                    backgroundColor: alpha(common.black, 0.78),
                    backgroundImage: `linear-gradient(180deg, ${alpha(
                        palette.primary.main,
                        0.06
                    )} 0%, transparent 100%)`,
                    boxShadow:
                        '0px 28px 60px rgba(0, 0, 0, 0.45), 0px 10px 24px rgba(0, 0, 0, 0.28)'
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: alpha(common.white, 0.08)
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: `1px solid ${alpha(common.white, options.borderAlpha)}`,
                    backgroundColor: alpha(common.black, 0.86),
                    backgroundImage: `linear-gradient(180deg, ${alpha(
                        palette.primary.main,
                        0.04
                    )} 0%, transparent 100%)`
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    paddingRight: '4px',
                    paddingLeft: '4px',
                    color: palette.text.secondary,
                    borderRadius: 4,
                    // Only a raised label sits on the field's border and needs a backing.
                    // Resting inside the field it showed as a box wherever the field is on a
                    // surface lighter than the paper colour (e.g. the terms page card).
                    '&.MuiInputLabel-shrink': {
                        backgroundColor: palette.background.paper
                    }
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    '&::placeholder': {
                        color: palette.text.secondary
                    }
                }
            }
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(common.white, 0.03),
                    borderColor: alpha(common.white, 0.12),
                    '&:hover': {
                        backgroundColor: alpha(common.white, 0.05),
                        borderColor: alpha(common.white, 0.2)
                    },
                    [`&.${filledInputClasses.disabled}`]: {
                        backgroundColor: 'transparent'
                    },
                    [`&.${filledInputClasses.focused}`]: {
                        backgroundColor: alpha(common.white, 0.04),
                        borderColor: palette.primary.main,
                        boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.16)}`
                    },
                    [`&.${filledInputClasses.error}`]: {
                        borderColor: palette.error.main,
                        boxShadow: `0 0 0 4px ${alpha(palette.error.main, 0.16)}`
                    }
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    transition:
                        'background-color 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                        backgroundColor: alpha(palette.primary.main, 0.08)
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(palette.primary.main, 0.12),
                        boxShadow: `inset 0 0 0 1px ${alpha(
                            palette.primary.main,
                            0.18
                        )}`
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: alpha(palette.primary.main, 0.16)
                    }
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    border: `1px solid ${alpha(common.white, options.overlayBorderAlpha)}`,
                    backgroundColor: alpha(common.black, 0.82),
                    boxShadow:
                        '0px 22px 46px rgba(0, 0, 0, 0.42), 0px 8px 18px rgba(0, 0, 0, 0.24)'
                },
                list: {
                    paddingTop: 6,
                    paddingBottom: 6
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    margin: '2px 6px',
                    '&:hover': {
                        backgroundColor: alpha(palette.primary.main, 0.08)
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(palette.primary.main, 0.12)
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: alpha(palette.primary.main, 0.16)
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(common.white, 0.02),
                    '&:hover': {
                        backgroundColor: alpha(common.white, 0.03),
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: alpha(common.white, 0.18)
                        }
                    },
                    [`&.${outlinedInputClasses.focused}`]: {
                        backgroundColor: alpha(common.white, 0.03),
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: palette.primary.main,
                            boxShadow: `0 0 0 4px ${alpha(
                                palette.primary.main,
                                0.16
                            )}`
                        }
                    },
                    [`&.${outlinedInputClasses.error}`]: {
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: palette.error.main,
                            boxShadow: `0 0 0 4px ${alpha(
                                palette.error.main,
                                0.16
                            )}`
                        }
                    }
                },
                notchedOutline: {
                    borderColor: alpha(common.white, 0.12)
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    border: `1px solid ${alpha(common.white, options.borderAlpha)}`,
                    backgroundColor: alpha(
                        palette.background.paper,
                        options.paperAlpha
                    ),
                    backgroundImage: `linear-gradient(180deg, ${alpha(
                        common.white,
                        0.008
                    )} 0%, transparent 100%)`
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    border: `1px solid ${alpha(common.white, options.overlayBorderAlpha)}`,
                    backgroundColor: alpha(common.black, 0.82)
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    color: palette.neutral[500]
                },
                track: {
                    backgroundColor: palette.neutral[400],
                    opacity: 1
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottomColor: alpha(common.white, 0.08)
                }
            }
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    [`& .${tableCellClasses.root}`]: {
                        backgroundColor: alpha(
                            palette.primary.main,
                            options.tableHeadAlpha
                        ),
                        color: palette.text.secondary,
                        fontWeight: 600
                    }
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    color: palette.text.secondary,
                    '&.Mui-selected': {
                        color: palette.text.primary
                    }
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 3,
                    borderRadius: 999,
                    backgroundColor: palette.primary.main,
                    boxShadow: `0px 4px 12px ${alpha(palette.primary.main, 0.28)}`
                }
            }
        },
        MuiTimelineConnector: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(common.white, 0.12)
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backdropFilter: 'blur(8px)',
                    background: alpha(common.black, 0.86),
                    border: `1px solid ${alpha(common.white, 0.1)}`
                }
            }
        }
    }
}
